import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import styles from "./DemoPage.module.css";
import c from "./ClinicalCase/ClinicalCase.module.css";
import PathwayGraph from "./ClinicalCase/PathwayGraph";

const pathway = {
  nodes: [
    {
      id: "n_genotype",
      label: "Suspected pathogenic mtDNA / nuclear variant (tissue-restricted)",
      type: "genotype",
      tier: "inferred",
      anchor: "hypothesis:h1",
    },
    {
      id: "n_oxphos",
      label:
        "Impaired oxidative phosphorylation / respiratory chain deficiency",
      type: "mechanism",
      tier: "established",
      anchor: "paper:36295831",
    },
    {
      id: "n_atp",
      label: "ATP depletion in high-demand neurons",
      type: "mechanism",
      tier: "inferred",
      anchor: "finding:imaging",
    },
    {
      id: "n_glyco",
      label: "Shift to anaerobic glycolysis",
      type: "mechanism",
      tier: "established",
      anchor: "paper:36685235",
    },
    {
      id: "n_cortex",
      label: "Posterior cortical & thalamic vulnerability",
      type: "tissue",
      tier: "established",
      anchor: "paper:36685235",
    },
    {
      id: "n_cortex_infer",
      label: "Cytotoxic edema (not ischemia/thrombosis)",
      type: "tissue",
      tier: "inferred",
      anchor: "finding:imaging",
    },
    {
      id: "n_bbb",
      label: "Secondary BBB disruption & reactive astrocytosis",
      type: "tissue",
      tier: "hypothesis",
      anchor: "finding:csf",
    },
    {
      id: "n_sle",
      label: "Stroke-like episodes (non-vascular)",
      type: "phenotype",
      tier: "established",
      anchor: "paper:36685235",
    },
    {
      id: "n_lactate",
      label: "Reversible exercise lactic acidosis (10.6 mmol/L)",
      type: "phenotype",
      tier: "inferred",
      anchor: "finding:lactate",
    },
    {
      id: "n_seizure",
      label: "Seizures / focal epileptiform discharges",
      type: "phenotype",
      tier: "established",
      anchor: "paper:36362003",
    },
    {
      id: "n_seizure_infer",
      label: "Regional cortical hyperexcitability",
      type: "phenotype",
      tier: "inferred",
      anchor: "finding:eeg",
    },
    {
      id: "n_wm",
      label: "White matter changes & high CSF protein",
      type: "phenotype",
      tier: "hypothesis",
      anchor: "finding:csf",
    },
    {
      id: "n_dx",
      label: "MELAS-spectrum disorder",
      type: "diagnosis",
      tier: "inferred",
      anchor: "hypothesis:h1",
    },
  ],
  edges: [
    {
      source: "n_genotype",
      target: "n_oxphos",
      tier: "inferred",
      evidence:
        "A tissue-restricted pathogenic variant would impair the respiratory chain; blood-negative testing does not exclude high muscle heteroplasmy.",
      anchor: "hypothesis:h1",
    },
    {
      source: "n_oxphos",
      target: "n_glyco",
      tier: "established",
      evidence:
        "Impaired respiratory chain function forces reliance on glycolytic ATP production during increased metabolic demand.",
      anchor: "paper:36295831",
    },
    {
      source: "n_glyco",
      target: "n_lactate",
      tier: "inferred",
      evidence:
        "The marked rise to 10.6 mmol/L with subsequent normalization confirms a reversible defect in oxidative metabolism, not a fixed metabolic block or hepatic clearance failure.",
      anchor: "finding:lactate",
    },
    {
      source: "n_oxphos",
      target: "n_atp",
      tier: "inferred",
      evidence:
        "Respiratory chain deficiency depletes ATP in neurons with the highest metabolic demand.",
      anchor: "finding:imaging",
    },
    {
      source: "n_atp",
      target: "n_cortex",
      tier: "established",
      evidence:
        "Stroke-like lesions characteristically spare major arterial territories and favor posterior cortical regions.",
      anchor: "paper:36685235",
    },
    {
      source: "n_atp",
      target: "n_cortex_infer",
      tier: "inferred",
      evidence:
        "DWI restriction, MRS lactate elevation, reduced NAA and normal MRA/MRV together indicate cytotoxic edema from ATP depletion in cortical and thalamic neurons, not ischemic infarction or venous thrombosis.",
      anchor: "finding:imaging",
    },
    {
      source: "n_cortex_infer",
      target: "n_sle",
      tier: "established",
      evidence:
        "Proton MRS detects elevated lactate within these lesions in ~90% of cases, reflecting the anaerobic shift underlying non-vascular stroke-like episodes.",
      anchor: "paper:36685235",
    },
    {
      source: "n_atp",
      target: "n_seizure",
      tier: "established",
      evidence:
        "Seizures occur in ~90% of primary mitochondrial disease, driven by cortical irritability from energy deficit and disrupted ion homeostasis.",
      anchor: "paper:36362003",
    },
    {
      source: "n_seizure",
      target: "n_seizure_infer",
      tier: "inferred",
      evidence:
        "Regional EEG abnormalities over the parieto-occipito-temporal lesions reflect neuronal hyperexcitability from impaired Na+/K+-ATPase function and glutamate clearance failure in energy-deprived cortex.",
      anchor: "finding:eeg",
    },
    {
      source: "n_cortex_infer",
      target: "n_bbb",
      tier: "hypothesis",
      evidence:
        "Recurrent cytotoxic edema and microvascular endothelial stress may trigger secondary BBB disruption and reactive astrocytosis, mimicking inflammatory demyelination on conventional MRI.",
      anchor: "finding:csf",
    },
    {
      source: "n_bbb",
      target: "n_wm",
      tier: "hypothesis",
      evidence:
        "If this holds, targeted CSF proteomics would show elevated MMP-9/MMP-2 and complement products without oligoclonal bands, and DTI/magnetization-transfer imaging would show axonal-sparing gliosis rather than primary myelin destruction.",
      anchor: "finding:csf",
    },
    {
      source: "n_sle",
      target: "n_dx",
      tier: "inferred",
      evidence:
        "Non-vascular stroke-like episodes with lactate elevation, seizures and encephalopathy converge on a MELAS-spectrum classification, pending tissue confirmation.",
      anchor: "hypothesis:h1",
    },
    {
      source: "n_lactate",
      target: "n_dx",
      tier: "inferred",
      evidence:
        "Reversible lactic acidosis on exertion supports the mitochondrial metabolic basis of the leading diagnosis.",
      anchor: "hypothesis:h1",
    },
    {
      source: "n_seizure",
      target: "n_dx",
      tier: "inferred",
      evidence:
        "Seizure burden is consistent with the encephalopathy component of MELAS.",
      anchor: "hypothesis:h1",
    },
  ],
  ungrounded: [],
  anchor_registry: {
    "finding:imaging":
      "investigation: DWI restriction, MRS lactate peak, reduced NAA, normal MRA/MRV",
    "finding:lactate":
      "investigation: exercise lactate 10.6 mmol/L, normalizing at rest",
    "finding:eeg":
      "investigation: focal epileptiform discharges over parieto-occipito-temporal region",
    "finding:csf":
      "investigation: persistently elevated CSF protein with white matter changes",
    "hypothesis:h1": "hypothesis: MELAS-spectrum disorder",
    "paper:36685235":
      "paper: Stroke-like episodes in mitochondrial disease — imaging and MRS (PMID 36685235)",
    "paper:36295831":
      "paper: Exercise lactate and respiratory chain dysfunction (PMID 36295831)",
    "paper:36362003":
      "paper: Seizures in primary mitochondrial disease (PMID 36362003)",
  },
};

const presentation = [
  {
    label: "Admission 1",
    value:
      "Unsteady gait, involuntary limb twitching, altered consciousness, neck stiffness, sluggish pupils. Markedly elevated CSF protein. MRI showed demyelination-type white matter changes in frontal and parietal lobes. Managed as presumed viral encephalitis.",
  },
  {
    label: "Admission 2",
    value:
      "2.5 years later: recurrent seizures, impaired consciousness, nystagmus, dysarthria, right-sided hemiparesis. Elevated CSF protein again. MRI showed patchy DWI hyperintensity in left medial temporal and occipital lobes with normal MRA. Treated as cerebral infarction.",
  },
  {
    label: "Admission 3",
    value:
      "Two days post-discharge: right limb immobility, tongue deviation, ataxia. MRA and MR venography unremarkable. DWI-bright signal in left parieto-occipito-temporal region with restricted diffusion in the left thalamus.",
  },
  {
    label: "Ancillary",
    badges: [
      "Exercise lactate 10.6 (normalised on rest)",
      "EEG regional epileptiform",
      "MRS lactate peak",
      "Reduced NAA + creatine",
    ],
  },
  {
    label: "Genetics",
    value:
      "Peripheral blood mtDNA point mutation panel — negative. No muscle biopsy or tissue-based genomic sequencing performed. Infectious, autoimmune, and inflammatory workups negative.",
  },
  {
    label: "Current Rx",
    value:
      "Stabilised on levetiracetam, pregabalin, low-dose lamotrigine, flunarizine, edaravone, and coenzyme Q10, with deliberate avoidance of sodium valproate.",
  },
];

const mechanism = [
  {
    tier: "ESTABLISHED",
    text: "Stroke-like episodes in mitochondrial disease arise from localised energy failure rather than vascular occlusion, producing lesions that spare major arterial territories and favour posterior cortical regions.",
    source: "PMID 36685235.",
  },
  {
    tier: "ESTABLISHED",
    text: "Proton MRS reliably detects elevated lactate peaks within these lesions in roughly 90% of cases, reflecting a shift to anaerobic glycolysis from impaired oxidative phosphorylation.",
    source: "PMID 36685235.",
  },
  {
    tier: "INFERRED",
    text: "The combination of DWI restriction, MRS lactate elevation, reduced N-acetylaspartate, and normal MRA/MRV indicates cytotoxic oedema driven by ATP depletion in high-demand cortical and thalamic neurons, rather than ischaemic infarction. The thalamic restricted diffusion aligns with the vulnerability of deep grey matter to mitochondrial energy deficits.",
  },
  {
    tier: "ESTABLISHED",
    text: "Impaired respiratory chain function forces reliance on glycolytic ATP during increased metabolic demand, leading to rapid lactate accumulation that clears on rest — as seen in the exercise-induced rise to 10.6 mmol/L and subsequent normalisation.",
    source: "PMID 36295831.",
  },
  {
    tier: "INFERRED",
    text: "The marked rise to 10.6 mmol/L with subsequent normalisation confirms a reversible defect in oxidative metabolism consistent with partial respiratory chain deficiency, rather than a fixed metabolic block or hepatic clearance failure.",
  },
  {
    tier: "ESTABLISHED",
    text: "Seizures occur in roughly 90% of primary mitochondrial diseases, driven by cortical irritability from energy deficit and disrupted ion homeostasis.",
    source: "PMID 36362003.",
  },
  {
    tier: "INFERRED",
    text: "Regional EEG abnormalities correlating with the parieto-occipito-temporal lesions reflect localised neuronal hyperexcitability from impaired Na+/K+-ATPase function and glutamate clearance failure in energy-deprived cortex.",
  },
  {
    tier: "HYPOTHESIS",
    text: "The prominent demyelination-type white matter changes and sustained CSF protein elevation are atypical for isolated mitochondrial energy failure. Proposed: recurrent cytotoxic oedema and microvascular endothelial stress trigger secondary blood-brain barrier disruption and reactive astrocytosis, mimicking inflammatory demyelination on conventional MRI.",
    prediction:
      "Targeted CSF proteomics would show elevated MMP-9 and MMP-2 and complement products without oligoclonal bands.",
    test: "DTI and magnetization transfer ratio would show axonal-sparing gliosis rather than primary myelin destruction.",
    decision:
      "Confirmation would clarify these features as downstream neurovascular injury from mitochondrial stress rather than a primary autoimmune leukoencephalopathy.",
  },
];

const investigations = [
  {
    heading: "Clinical priority",
    items: [
      {
        title: "Mitochondrial safety prescribing audit",
        tag: "Urgent",
        why: "Formal EHR alert to permanently restrict known mitochondrial toxins — sodium valproate, linezolid, aminoglycosides, certain statins, platinum chemotherapeutics. These inhibit mitochondrial translation, impair fatty acid oxidation, or deplete coenzyme Q10, precipitating acute decompensation.",
        decision:
          "Any prescribed agent must be cross-referenced against mitochondrial toxicity databases before initiation.",
      },
      {
        title: "Muscle biopsy with fresh-frozen tissue banking",
        why: "Histology (Gomori trichrome, SDH, COX) with fresh-frozen aliquots for respiratory chain enzymatic assay and mtDNA / nuclear gene sequencing. Blood screening failed; muscle is the gold-standard tissue for detecting heteroplasmic variants.",
        decision:
          "Above 10% COX-negative fibers or a pathogenic variant confirms mitochondrial disease.",
      },
      {
        title: "Targeted nuclear mitochondrial gene panel",
        why: "Focus on POLG, TWNK, RRM2B, SUCLA2, SUCLG1, SURF1. The negative blood mtDNA panel and adult onset increase the probability of a nuclear-encoded maintenance defect.",
        decision:
          "Biallelic pathogenic variants shift the diagnosis to a nuclear mitochondrial disorder.",
      },
    ],
  },
  {
    heading: "Research and translational",
    items: [
      {
        title: "Single-cell heteroplasmy mapping in PBMCs and skin fibroblasts",
        why: "Answers whether tissue-specific segregation and clonal expansion explain the negative blood panel. Low bulk heteroplasmy masking high single-cell loads would preserve mitochondrial disease likelihood.",
      },
      {
        title: "Dynamic proton MRS lactate clearance kinetics",
        why: "Measures the rate constant of lactate washout post-exercise. A delayed clearance curve (>20 min to baseline) would functionally quantify oxidative phosphorylation reserve capacity.",
      },
    ],
  },
  {
    heading: "Model-proposed novel",
    items: [
      {
        title: "CSF mtDNA copy number and oxidative stress metabolomics",
        why: "Investigates the persistently elevated CSF protein and white matter changes. Measures cell-free mtDNA fragments, 8-OHdG, and acylcarnitine profiles. Predicted: elevated cell-free mtDNA and oxidative damage markers without inflammatory cytokine predominance, indicating neurovascular barrier leakage from mitochondrial stress rather than autoimmunity.",
      },
    ],
  },
];

const sections = [
  { id: "case", label: "The case" },
  { id: "variant", label: "Variant interpretation" },
  { id: "pathway", label: "Reasoning pathway" },
  { id: "mechanism", label: "Mechanistic chain" },
  { id: "diagnosis", label: "Diagnosis" },
  { id: "change", label: "What would change it" },
  { id: "investigations", label: "Investigations" },
  { id: "adversarial", label: "Adversarial testing" },
];

const TIER = {
  ESTABLISHED: c.tierEstablished,
  INFERRED: c.tierInferred,
  HYPOTHESIS: c.tierHypothesis,
};

export default function MELASClinicalDemo() {
  const [active, setActive] = useState("case");

  useEffect(() => {
    const opts = { rootMargin: "-30% 0px -60% 0px", threshold: 0 };
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setActive(e.target.id);
      });
    }, opts);
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el)
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY - 100,
        behavior: "smooth",
      });
  };

  return (
    <div className={styles.page}>
      <div className={styles.background}>
        <div className={styles.glow} />
      </div>

      <div className={c.shell}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link to="/" className={styles.backLink}>
            &larr; Back to Home
          </Link>

          <div className={c.header}>
            <span className={c.tag}>MitoAgent · Live Demo</span>
            <h1 className={c.title}>
              MELAS —{" "}
              <span className={c.highlight}>
                a negative blood panel that did not exclude disease
              </span>
            </h1>
            <p className={c.subtitle}>
              Three admissions over 2.5 years · woman in her early fifties ·
              serologically negative
            </p>
            <div className={c.statsRow}>
              <div className={c.statItem}>
                <span className={c.statLabel}>Papers</span>
                <span className={c.statValue}>21</span>
              </div>
              <div className={c.statItem}>
                <span className={c.statLabel}>RAG chunks</span>
                <span className={c.statValue}>385</span>
              </div>
              <div className={c.statItem}>
                <span className={c.statLabel}>Deployment</span>
                <span className={c.statValue}>Local</span>
              </div>
            </div>
          </div>
        </motion.div>

        <div className={c.layout}>
          <nav className={c.nav}>
            <div className={c.navInner}>
              {sections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className={`${c.navItem} ${active === s.id ? c.navActive : ""}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </nav>

          <div className={c.content}>
            {/* THE CASE */}
            <section id="case" className={c.section}>
              <h2 className={c.sectionTitle}>The case</h2>
              <table className={c.dataTable}>
                <tbody>
                  {presentation.map((row) => (
                    <tr key={row.label}>
                      <td className={c.dataLabel}>{row.label}</td>
                      <td className={c.dataValue}>
                        {row.badges ? (
                          <div className={c.badges}>
                            {row.badges.map((b) => (
                              <span key={b} className={c.badge}>
                                {b}
                              </span>
                            ))}
                          </div>
                        ) : (
                          row.value
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            {/* VARIANT INTERPRETATION */}
            <section id="variant" className={c.section}>
              <h2 className={c.sectionTitle}>Variant interpretation</h2>
              <div className={c.assessment}>
                <p className={c.assessmentLabel}>
                  Negative blood panel does not exclude disease
                </p>
                <p className={c.assessmentText}>
                  The negative peripheral blood mtDNA point-mutation panel
                  reflects a limitation of sampling, not absence of disease.
                  Mitochondrial diseases exhibit profound tissue-specific
                  heteroplasmy: pathogenic variants can reach high loads in
                  skeletal muscle, brain, or heart while remaining below the
                  detection threshold in circulating lymphocytes. Blood
                  screening misses a substantial fraction of confirmed
                  mitochondrial disorders, particularly nuclear-encoded defects
                  and low-peripheral-heteroplasmy mtDNA variants. Definitive
                  exclusion requires muscle or buccal mucosa sequencing.
                </p>
              </div>
            </section>

            {/* PATHWAY */}
            <section id="pathway" className={c.section}>
              <h2 className={c.sectionTitle}>Reasoning pathway</h2>
              <PathwayGraph graph={pathway} />
            </section>

            {/* MECHANISTIC CHAIN */}
            <section id="mechanism" className={c.section}>
              <h2 className={c.sectionTitle}>Mechanistic chain</h2>
              {mechanism.map((m, i) => (
                <div key={i} className={c.mechItem}>
                  <span
                    className={`${c.tier} ${TIER[m.tier] || c.tierInferred}`}
                  >
                    {m.tier}
                  </span>
                  <p className={c.mechText}>{m.text}</p>
                  {m.source && (
                    <p className={c.mechSource}>Source: {m.source}</p>
                  )}
                  {m.prediction && (
                    <div className={c.prediction}>
                      <p>
                        <strong>Testable prediction:</strong> {m.prediction}
                      </p>
                      {m.test && (
                        <p>
                          <strong>Proposed test:</strong> {m.test}
                        </p>
                      )}
                      {m.decision && (
                        <p>
                          <strong>Decision rule:</strong> {m.decision}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </section>

            {/* DIAGNOSIS */}
            <section id="diagnosis" className={c.section}>
              <h2 className={c.sectionTitle}>Diagnosis</h2>
              <div
                className={c.assessment}
                style={{ borderLeftColor: "hsl(var(--primary))" }}
              >
                <p
                  className={c.assessmentLabel}
                  style={{ color: "hsl(var(--primary))" }}
                >
                  Leading diagnosis — MELAS · Medium confidence
                </p>
                <p className={c.assessmentText}>
                  Driven by the classic triad of stroke-like episodes,
                  documented lactic acidosis (serum, exercise, and MRS-derived),
                  and encephalopathy with seizures. The posterior cortical and
                  thalamic DWI restriction without vascular occlusion and the
                  MRS lactate peak align with established mitochondrial
                  encephalopathy. Confidence is held at medium — not high —
                  because of the negative blood panel, absent muscle histology,
                  atypical CSF protein elevation, prominent white matter
                  changes, and lack of maternal family history. Tissue-level
                  validation is required before definitive classification.
                </p>
              </div>
            </section>

            {/* WHAT WOULD CHANGE IT */}
            <section id="change" className={c.section}>
              <h2 className={c.sectionTitle}>
                What would change this diagnosis
              </h2>
              <div className={c.mechItem}>
                <span className={`${c.tier} ${c.tierEstablished}`}>
                  Confirmatory
                </span>
                <p className={c.mechText}>
                  Muscle biopsy showing ragged red fibers, COX-negative fibers,
                  and/or NGS of muscle identifying a pathogenic mtDNA variant
                  (e.g. MT-TL1 m.3243A&gt;G) or biallelic pathogenic variants in
                  nuclear genes (POLG, TWNK, RRM2B). A heteroplasmy load above
                  10% in muscle or a known pathogenic allele would confirm the
                  diagnosis.
                </p>
              </div>
              <div className={c.mechItem}>
                <span className={`${c.tier} ${c.tierHypothesis}`}>
                  Refuting
                </span>
                <p className={c.mechText}>
                  Normal muscle biopsy with intact respiratory chain enzyme
                  activities and no ragged red or COX-negative fibers, combined
                  with whole-exome/genome sequencing revealing an alternative
                  etiology (CADASIL, MOG antibody-associated disease, or primary
                  autoimmune encephalitis). Complete resolution of MRS lactate
                  peaks and DWI lesions on serial imaging without metabolic
                  intervention would also argue against a primary mitochondrial
                  disorder.
                </p>
              </div>
            </section>

            {/* INVESTIGATIONS */}
            <section id="investigations" className={c.section}>
              <h2 className={c.sectionTitle}>Next investigations</h2>
              {investigations.map((group, gi) => (
                <div key={gi} className={c.invGroup}>
                  <p className={c.invHeading}>{group.heading}</p>
                  {group.items.map((inv, ii) => (
                    <div key={ii} className={c.invCard}>
                      <p className={c.invTitle}>{inv.title}</p>
                      {inv.why && (
                        <p className={c.invWhy}>
                          <strong>Why:</strong> {inv.why}
                        </p>
                      )}
                      {inv.decision && (
                        <p className={c.invDecision}>
                          <strong>Decision rule:</strong> {inv.decision}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </section>

            {/* ADVERSARIAL — single summary block */}
            <section id="adversarial" className={c.section}>
              <h2 className={c.sectionTitle}>Adversarial testing</h2>
              <div className={c.advCard}>
                <div className={c.advHead}>
                  <span className={c.advHyp}>MELAS-spectrum disorder</span>
                  <span className={`${c.advResult} ${c.resultSurvives}`}>
                    Weakened, survives
                  </span>
                </div>
                <p className={c.advLine}>
                  <strong>Strongest argument against:</strong> The prominent
                  demyelination-type white matter changes and markedly elevated
                  CSF protein are atypical for classic mitochondrial energy
                  failure, which spares deep white matter. Onset in the early
                  fifties with a noncontributory family history deviates from
                  typical maternal inheritance.
                </p>
                <p className={c.advLine}>
                  <strong>Why it survives:</strong> Adult-onset cases and
                  tissue-segregated heteroplasmy can obscure classic inheritance
                  and presentation. The evidence weakens but does not refute the
                  hypothesis.
                </p>
                <p className={c.advLine}>
                  <strong>Best refutation test:</strong> Comprehensive muscle
                  biopsy with histology, respiratory chain enzymatic assay, and
                  parallel whole-exome sequencing. Normal muscle biochemistry
                  plus an alternative genetic or autoimmune diagnosis would
                  refute MELAS.
                </p>
              </div>
            </section>

            <div className={c.disclaimer}>
              Research tool only. MitoAgent is not a diagnostic system and does
              not constitute medical advice. All clinical decisions must be made
              by qualified healthcare professionals. Outputs should be
              independently verified by domain experts. This demo uses a
              synthetic case for illustrative purposes.
            </div>

            <div className={styles.cta}>
              <p className={styles.ctaText}>
                Run your own case through MitoAgent.
              </p>
              <Link to="/playground" className={styles.ctaButton}>
                Apply for Early Access &rarr;
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
